import { dashboardKeys, followingFeedKeys, nearbyFeedKeys } from '@/features/events/feed/feedQuery';
import { citywideEventKeys } from '@/features/events/feed/useCitywideEvents';
import { editableEventKeys } from '@/features/events/useEditableEvent';
import { eventDetailKeys } from '@/features/events/useEventDetail';
import { useAuth } from '@/lib/auth/auth';
import type {
  EventImageAsset,
  EventRecord,
  GeocodeRequest,
  GeocodeResult,
  GeocodeSuggestion,
  PatchEventPosterFocalPointRequest,
} from '@/lib/api/apiTypes';
import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';

import type { EventEditorMode, EventMutationPayload } from './types';
import { getEventSaveRequest, syncSavedEventCaches } from './utils';

export function useEventEditorTransport() {
  const request = useAuth((state) => state.request);
  const queryClient = useQueryClient();

  const autocompleteAddress = React.useCallback(
    (query: string, coords?: { latitude?: number; longitude?: number }) => {
      const params = new URLSearchParams({ q: query });
      if (coords?.latitude && coords?.longitude) {
        params.set('lat', String(coords.latitude));
        params.set('lng', String(coords.longitude));
      }
      return request<GeocodeSuggestion[]>(`/geocode/autocomplete?${params.toString()}`);
    },
    [request]
  );

  const geocodeAddress = React.useCallback(
    (address: string) =>
      request<GeocodeResult>('/geocode', {
        method: 'POST',
        body: JSON.stringify({ address } satisfies GeocodeRequest),
      }),
    [request]
  );

  const savePosterFocalPoint = React.useCallback(
    (
      eventID: string,
      imageID: string,
      payload: PatchEventPosterFocalPointRequest
    ) =>
      request<EventImageAsset>(`/events/${eventID}/poster-images/${imageID}/focal-point`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    [request]
  );

  const saveEvent = React.useCallback(
    async ({
      eventID,
      mode,
      payload,
    }: {
      eventID?: string;
      mode: EventEditorMode;
      payload: EventMutationPayload;
    }) => {
      const saveRequest = getEventSaveRequest(mode, eventID);
      const event = await request<EventRecord>(
        saveRequest.path,
        {
          method: saveRequest.method,
          body: JSON.stringify(payload),
        }
      );

      syncSavedEventCaches(queryClient, event);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: nearbyFeedKeys.all }),
        queryClient.invalidateQueries({ queryKey: followingFeedKeys.all }),
        queryClient.invalidateQueries({ queryKey: citywideEventKeys.all }),
        queryClient.invalidateQueries({ queryKey: dashboardKeys.current() }),
        queryClient.invalidateQueries({ queryKey: editableEventKeys.all }),
        queryClient.invalidateQueries({ queryKey: eventDetailKeys.byId(event.id) }),
      ]);

      return event;
    },
    [queryClient, request]
  );

  return React.useMemo(
    () => ({
      autocompleteAddress,
      geocodeAddress,
      saveEvent,
      savePosterFocalPoint,
    }),
    [autocompleteAddress, geocodeAddress, saveEvent, savePosterFocalPoint]
  );
}
